import useDealShortId from '@/hooks/use-deal-short-id';
import {
  assignedTaskStore,
  auditStore,
  commentStore,
  confirmationHistoryStore,
  confirmationsStore,
  coRegistrantStore,
  customerAddressStore,
  dealStore,
  fileMetadataStore,
  paymentStore,
  priceBreakdownStore,
  primaryCustomerStore,
  protectionPlanStore,
  replyStore,
  taskStore,
  userStore,
  vehicleFromIndexerStore,
  vehicleListingStore,
  vehicleLocationStore,
} from '@/models/stores';
import { taskStoreV2, taskTemplateStore } from '@/models/stores/tasks-v2.ts';
import { compact } from 'lodash';
import { runInAction, toJS } from 'mobx';
import { useEffect } from 'react';

const useDealPageHydrateLocal = () => {
  const { shortId } = useDealShortId();

  useEffect(() => {
    if (!shortId) {
      return;
    }

    // Hydrate the specific deal first
    // TODO: we can flatten the logic multiple ways for readability
    const hydrateDeal = async () => {
      // Performance note: this is ~850ms

      await Promise.all([
        dealStore.hydrateDeal(shortId), // 200ms to 300ms
      ]).then(() => {
        const currentDeal = dealStore.getByShortId(shortId);

        if (!currentDeal) {
          return;
        }

        Promise.all([
          // TODO: might need to add the task hydration here
          assignedTaskStore.hydrateDeal(currentDeal.id),
          taskStoreV2.hydrateDeal(currentDeal.id),
          primaryCustomerStore.hydrateDeal(currentDeal.id),
          coRegistrantStore.hydrateDeal(currentDeal.id),

          protectionPlanStore.hydrateDeal(currentDeal.id),
          priceBreakdownStore.hydrateDeal(currentDeal.id),
          auditStore.hydrateDeal(currentDeal.id),
          commentStore.hydrateDeal(currentDeal.id),
          fileMetadataStore.hydrateDeal(currentDeal.id),
          vehicleListingStore.hydrateDeal(currentDeal.vehicle_listing_id),
          paymentStore.hydrateDeal(currentDeal.id),
        ]).then(() => {
          const currentDeal = dealStore.getByShortId(shortId);

          if (!currentDeal) {
            return;
          }

          const customerIds = compact([
            currentDeal.primary_customer?.id,
            currentDeal.co_registrant?.id,
          ]);

          const commentIds = currentDeal.comments.map((comment) => comment.id);
          const vehicleId = currentDeal.vehicle_listing?.vehicle_id;

          Promise.all([
            customerAddressStore.hydrateDeal(customerIds), // TODO: can be optimized
            replyStore.hydrateDeal(commentIds), // TODO: can be optimized
            confirmationsStore.hydrateDeal(currentDeal.id),

            vehicleId && vehicleFromIndexerStore.hydrateDeal(vehicleId),
            taskStore.hydrateLocal(),
            userStore.hydrateLocal(), // Need all the users for the tasks assignees
            vehicleLocationStore.hydrateLocal(), // Need all the locations for the current vehicle location
            taskTemplateStore.hydrateLocal(),
          ]).then(() => {
            const currentDeal = dealStore.getByShortId(shortId);

            if (!currentDeal) {
              return;
            }

            const confirmations = toJS(
              confirmationsStore.storeIndexedByDealId.get(currentDeal.id),
            );

            const confirmationIds: string[] = [];
            if (confirmations) {
              confirmations.forEach((confirmation) =>
                confirmationIds.push(confirmation.id),
              );
            }

            Promise.all([
              confirmationHistoryStore.hydrateDeal(confirmationIds ?? []),
            ]);
          });
        });
      });
    };

    runInAction(async () => {
      await hydrateDeal();
    });
  }, []);
};

export default useDealPageHydrateLocal;
